import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO, isPast, isToday, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import {
  LayoutGrid, CalendarDays, Image as ImageIcon, BarChart3, Inbox,
  ChevronRight, Instagram, Facebook, CheckCircle2, Eye, Video, Users, Zap,
  Heart, MessageCircle, Plus, Sparkles, Maximize2, FileText,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, AreaChart, Area } from "recharts";
import api, { apiError } from "@/lib/api";
import { SafeChart } from "@/components/SafeChart";
import { CmAnalyse } from "@/pages/cm/Analyse";
import StatusBadge from "@/components/StatusBadge";
import NewPublicationModal from "@/components/NewPublicationModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GlobalCalendar from "@/pages/GlobalCalendar";
import Contenus from "@/pages/Contenus";
import { formatLabel, platformLabel, frDate, clientRequestStatus, isCmClient, pubVisualFull, isFeedVisible } from "@/lib/constants";
import { toast } from "sonner";

const PASTEL = {
  blue: { bg: "#E0F2FE", text: "#0369A1", border: "#BAE6FD" },
  amber: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  purple: { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
  rose: { bg: "#FFE4E6", text: "#BE123C", border: "#FECDD3" },
  green: { bg: "#DCFCE7", text: "#15803D", border: "#BBF7D0" },
};

const pubVisual = (p) => pubVisualFull(p).src;
const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const relWhen = (iso) => {
  if (!iso) return "";
  try {
    const mins = Math.max(0, Math.round((Date.now() - parseISO(iso).getTime()) / 60000));
    if (mins < 60) return `il y a ${mins} min`;
    const h = Math.round(mins / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.round(h / 24)} j`;
  } catch { return ""; }
};
const formatK = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace(".", ",")} K` : String(n));
const delta = (cur, prevv) => {
  if (prevv > 0) { const p = Math.round(((cur - prevv) / prevv) * 100); return { text: `${p >= 0 ? "+" : ""}${p} % vs mois dernier`, pos: p >= 0 }; }
  return cur > 0 ? { text: "nouveau ce mois", pos: true } : null;
};
const subOf = (d, fallback) => (d ? { text: d.text, color: d.pos ? "#15803D" : "#BE123C" } : fallback ? { text: fallback, color: "#94A3B8" } : null);

function Thumb({ src, name, color, className = "w-9 h-9 rounded-lg" }) {
  const [err, setErr] = useState(false);
  if (src && !err) return <img src={src} alt="" loading="lazy" onError={() => setErr(true)} className={`${className} object-cover shrink-0 border border-neutral-100`} />;
  return <span className={`${className} flex items-center justify-center text-[10px] font-bold text-white shrink-0`} style={{ background: color || "#94A3B8" }}>{initials(name)}</span>;
}

function Card({ icon: Icon, title, link, linkLabel, count, children, testid, className = "", action }) {
  return (
    <div className={`bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden flex flex-col ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100">
        <Icon className="w-4 h-4 text-[#0F172A]" strokeWidth={2} />
        <h3 className="font-heading font-bold text-[13px] text-[#0F172A]">{title}</h3>
        {count != null && <span className="text-[10px] text-neutral-400 bg-neutral-100 rounded-full px-1.5 py-0.5">{count}</span>}
        {link && (
          <Link to={link} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline flex items-center gap-0.5 shrink-0" data-testid={`${testid}-see-all`}>
            {linkLabel || "Voir tout"} <ChevronRight className="w-3 h-3" />
          </Link>
        )}
        {!link && action && <span className="ml-auto shrink-0">{action}</span>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Kpi({ testid, icon: Icon, pastel, value, label, sub }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-3.5 py-3 flex gap-3 items-center" data-testid={testid}>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center border shrink-0" style={{ background: pastel.bg, color: pastel.text, borderColor: pastel.border }}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-[#475569]">{label}</p>
        <p className="font-heading text-xl font-bold text-[#0F172A] leading-tight flex items-baseline gap-1.5">
          {value}
          {sub && <span className="text-[11px] font-medium" style={{ color: sub.color || "#15803D" }} data-testid={`${testid}-sub`}>{sub.text}</span>}
        </p>
      </div>
    </div>
  );
}

/* ================= Vue d'ensemble ================= */
function CmOverview({ dash, pubs, clients, requests, onGoTab, onTaskDone, clientId = "" }) {
  const navigate = useNavigate();
  const month = format(new Date(), "yyyy-MM");
  const lastMonth = format(subMonths(new Date(), 1), "yyyy-MM");
  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const [ig, setIg] = useState(null);
  useEffect(() => {
    if (clientId) { setIg(null); return; } // vue client : pas d'agrégat Meta multi-clients
    api.get(`/instagram/insights/overview?month=${month}`).then((r) => setIg(r.data)).catch(() => setIg({ connected_clients: 0 }));
  }, [month, clientId]);

  const monthPubs = pubs.filter((p) => (p.date || "").startsWith(month));
  const lastMonthPubs = pubs.filter((p) => (p.date || "").startsWith(lastMonth));
  const reels = monthPubs.filter((p) => p.format === "reel").length;
  const reelsLast = lastMonthPubs.filter((p) => p.format === "reel").length;
  const pending = pubs.filter((p) => p.status === "a_valider").length;
  const cmClients = clients.filter((c) => !c.archived && isCmClient(c));

  /* Insights Meta agrégés (tous clients connectés) */
  const igConnected = !clientId && (ig?.connected_clients || 0) > 0;
  const interactions = (ig?.totals.likes || 0) + (ig?.totals.comments || 0) + (ig?.totals.saved || 0) + (ig?.totals.shares || 0);
  const prevInteractions = (ig?.prev_totals.likes || 0) + (ig?.prev_totals.comments || 0) + (ig?.prev_totals.saved || 0) + (ig?.prev_totals.shares || 0);
  const engRate = ig?.totals.reach > 0 ? (interactions / ig.totals.reach) * 100 : null;
  const prevEngRate = ig?.prev_totals?.reach > 0 ? (prevInteractions / ig.prev_totals.reach) * 100 : null;
  const mediaByIgId = useMemo(() => Object.fromEntries((ig?.media || []).map((mm) => [mm.media_id, mm])), [ig]);
  const metricsOf = (p) => mediaByIgId[p.channels_status?.instagram?.media_id];

  /* Jour courant (mini calendrier éditorial du jour) */
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const todayPubs = useMemo(() => pubs.filter((p) => p.date === todayKey)
    .sort((a, b) => (a.time || "").localeCompare(b.time || "")), [pubs, todayKey]);

  /* Série réelle du mois : prévues vs publiées par jour */
  const daily = useMemo(() => {
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = `${month}-${String(i + 1).padStart(2, "0")}`;
      const dayPubs = pubs.filter((p) => p.date === d);
      return { jour: i + 1, prevues: dayPubs.length, publiees: dayPubs.filter((p) => p.status === "publie").length };
    });
  }, [pubs, month]);

  const recents = useMemo(() => pubs.filter((p) => p.status === "publie")
    .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || ""))), [pubs]);
  const [recentFilter, setRecentFilter] = useState("tous");
  const recentShown = recents.filter((p) =>
    recentFilter === "tous" ? true
    : recentFilter === "posts" ? ["publication", "carrousel", "article_linkedin"].includes(p.format)
    : recentFilter === "reels" ? p.format === "reel"
    : p.format === "story"
  ).slice(0, 4);

  const todayTasks = (dash?.tasks || []).filter((t) => !t.archived && !["finalise", "livre_client"].includes(t.status) && t.deadline && t.deadline.slice(0, 10) <= format(new Date(), "yyyy-MM-dd")).slice(0, 5);
  const completeTask = async (t) => { await api.patch(`/tasks/${t.id}`, { status: "finalise" }); toast.success("Tâche finalisée"); onTaskDone?.(); };
  const openRequests = useMemo(() => (requests || []).filter((r) => !["terminee", "annulee", "refusee"].includes(r.status)).slice(0, 4), [requests]);
  const pendingPubs = (dash?.pending_validation || []).slice(0, 4);

  const pubsOfClientMonth = (cid) => monthPubs.filter((p) => p.client_id === cid).length;

  return (
    <div className="space-y-3" data-testid="cm-overview">
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cm-kpis">
        <Kpi testid="cm-kpi-prevues" icon={CalendarDays} pastel={PASTEL.blue} value={monthPubs.length} label="Publications prévues"
          sub={subOf(delta(monthPubs.length, lastMonthPubs.length))} />
        <Kpi testid="cm-kpi-valider" icon={Eye} pastel={PASTEL.amber} value={pending} label="Contenus à valider"
          sub={{ text: "en attente client", color: "#B45309" }} />
        <Kpi testid="cm-kpi-reels" icon={Video} pastel={PASTEL.green} value={reels} label="Réels du mois"
          sub={subOf(delta(reels, reelsLast))} />
        <Kpi testid="cm-kpi-engagement" icon={CheckCircle2} pastel={PASTEL.purple}
          value={engRate != null ? `${engRate.toFixed(1).replace(".", ",")} %` : "—"} label="Taux d'engagement"
          sub={engRate != null
            ? (prevEngRate != null
              ? { text: `${(engRate - prevEngRate) >= 0 ? "+" : ""}${(engRate - prevEngRate).toFixed(1).replace(".", ",")} pt vs mois dernier`, color: (engRate - prevEngRate) >= 0 ? "#15803D" : "#BE123C" }
              : { text: `${formatK(interactions)} interactions`, color: "#6B21A8" })
            : { text: "comptes Instagram non connectés", color: "#94A3B8" }} />
        <Kpi testid="cm-kpi-portee" icon={Users} pastel={PASTEL.rose}
          value={igConnected ? formatK(ig.totals.reach) : "—"} label="Portée totale"
          sub={igConnected ? subOf(delta(ig.totals.reach, ig.prev_totals.reach), "tous clients confondus") : { text: "insights Meta indisponibles", color: "#94A3B8" }} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendrier éditorial du jour */}
        <Card icon={CalendarDays} title="Calendrier éditorial du jour" link="/cm?tab=calendrier" linkLabel="Voir le calendrier" testid="cm-today">
          <div className="px-4 py-1.5 text-[11px] font-semibold text-neutral-500 border-b border-neutral-100 capitalize" data-testid="cm-today-date">
            {format(new Date(), "EEEE d MMMM", { locale: fr })}
          </div>
          <div className="max-h-[290px] overflow-y-auto">
            {todayPubs.map((p) => (
              <button key={p.id} data-testid={`cm-today-pub-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
                className="w-full flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <span className="text-[11px] font-semibold text-[#475569] w-9 shrink-0">{p.time}</span>
                <span className="flex gap-0.5 shrink-0">
                  {(p.channels?.instagram ?? p.platform !== "facebook") && <Instagram className="w-3 h-3 text-[#E1306C]" />}
                  {(p.channels?.facebook ?? p.platform === "facebook") && <Facebook className="w-3 h-3 text-[#1877F2]" />}
                </span>
                <Thumb src={pubVisual(p)} name={p.title} color={clientById[p.client_id]?.color} className="w-7 h-7 rounded-md" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-[#0F172A] truncate">{p.title}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{clientById[p.client_id]?.name} · {formatLabel(p.format)}</p>
                </div>
                <StatusBadge status={p.status} short className="!text-[9px] !px-1.5 !py-0 shrink-0" />
              </button>
            ))}
            {todayPubs.length === 0 && (
              <p className="px-4 py-6 text-[13px] text-neutral-400" data-testid="cm-today-empty">Aucune publication prévue aujourd'hui.</p>
            )}
          </div>
        </Card>

        {/* Contenus récents */}
        <Card icon={ImageIcon} title="Contenus récents" link="/cm?tab=contenus" linkLabel="Voir tous les contenus" testid="cm-recents">
          <div className="flex gap-1 px-4 pt-2 pb-1" data-testid="cm-recents-filters">
            {[["tous", "Tous"], ["posts", "Posts"], ["reels", "Réels"], ["stories", "Stories"]].map(([id, l]) => (
              <button key={id} data-testid={`cm-recents-${id}`} onClick={() => setRecentFilter(id)}
                className={`text-[11px] font-medium rounded-full px-2.5 py-1 transition-colors ${recentFilter === id ? "bg-[#0B132B] text-white" : "text-neutral-500 hover:bg-neutral-100"}`}>{l}</button>
            ))}
          </div>
          {recentShown.length === 0 && <p className="px-4 py-5 text-xs text-neutral-400">Aucun contenu publié pour le moment.</p>}
          {recentShown.map((p) => {
            const m = metricsOf(p);
            return (
              <button key={p.id} data-testid={`cm-recent-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
                className="w-full flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <Thumb src={pubVisual(p)} name={p.title} color={clientById[p.client_id]?.color} className="w-11 h-11 rounded-lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-[#0F172A] truncate">{clientById[p.client_id]?.name || "—"}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{platformLabel(p.platform)} · {formatLabel(p.format)} · {frDate(p.date)}</p>
                  {m && (
                    <p className="flex items-center gap-2.5 mt-0.5 text-[10px] text-neutral-500" data-testid={`cm-recent-metrics-${p.id}`}>
                      <span className="flex items-center gap-0.5"><Heart className="w-3 h-3 text-[#E1306C]" />{formatK(m.likes)}</span>
                      <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3 text-[#0369A1]" />{formatK(m.comments)}</span>
                      <span className="flex items-center gap-0.5"><Eye className="w-3 h-3 text-neutral-400" />{formatK(m.views)} vues</span>
                    </p>
                  )}
                </div>
                <StatusBadge status={p.status} short className="!text-[9px] !px-1.5 !py-0 shrink-0" />
              </button>
            );
          })}
        </Card>

        {/* Clients CM */}
        <Card icon={Users} title="Clients CM" link="/clients" linkLabel="Voir tous les clients" testid="cm-clients" count={cmClients.length}>
          {cmClients.length === 0 && <p className="px-4 py-6 text-[13px] text-neutral-400">Aucun client CM actif.</p>}
          {cmClients.slice(0, 6).map((c) => {
            const count = pubsOfClientMonth(c.id);
            const pct = c.objective_month ? Math.min(100, Math.round((count / c.objective_month) * 100)) : null;
            return (
              <button key={c.id} data-testid={`cm-client-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <Thumb src={c.brand?.logo_url} name={c.name} color={c.color} className="w-9 h-9 rounded-full" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-[#0F172A] truncate">{c.name}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{c.sector || "Community Management"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] font-bold text-[#0F172A]">{count}</p>
                  <p className="text-[9px] text-neutral-400">publications ce mois{pct != null ? ` · ${pct} %` : ""}</p>
                </div>
              </button>
            );
          })}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Performance du mois — données réelles publications */}
        <Card icon={BarChart3} title="Performance du mois" link="/cm?tab=analyse" linkLabel="Voir l'analyse" testid="cm-performance">
          {igConnected && ig.daily.length > 0 ? (
            <>
              <div className="flex gap-4 px-4 pt-2.5 text-[11px]" data-testid="cm-performance-totals">
                <span className="flex items-center gap-1.5 text-[#475569]"><span className="w-2 h-2 rounded-full bg-[#0369A1]" /> Portée <strong className="text-[#0F172A]">{formatK(ig.totals.reach)}</strong></span>
                <span className="flex items-center gap-1.5 text-[#475569]"><span className="w-2 h-2 rounded-full bg-[#22C55E]" /> Vues <strong className="text-[#0F172A]">{formatK(ig.totals.views)}</strong></span>
                <span className="flex items-center gap-1.5 text-[#475569]"><span className="w-2 h-2 rounded-full bg-[#B45309]" /> Interactions <strong className="text-[#0F172A]">{formatK(interactions)}</strong></span>
                {(() => { const d = delta(ig.totals.reach, ig.prev_totals.reach); return d && (
                  <span className="ml-auto font-semibold" style={{ color: d.pos ? "#15803D" : "#BE123C" }}>{d.text}</span>
                ); })()}
              </div>
              <div className="px-2 pb-2 pt-1" data-testid="cm-performance-chart">
                <SafeChart height={160}>
                  <AreaChart data={ig.daily.map((d) => ({ jour: parseInt(d.day.slice(8), 10), portee: d.reach, vues: d.views }))} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gPortee" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0369A1" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#0369A1" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gVues" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22C55E" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#22C55E" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="jour" tick={{ fontSize: 9, fill: "#94A3B8" }} interval={6} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <RTooltip formatter={(v, n) => [formatK(v), n === "portee" ? "Portée" : "Vues"]}
                      labelFormatter={(l) => `${l} ${format(new Date(), "MMMM", { locale: fr })}`} />
                    <Area type="monotone" dataKey="portee" stroke="#0369A1" strokeWidth={2} fill="url(#gPortee)" />
                    <Area type="monotone" dataKey="vues" stroke="#22C55E" strokeWidth={2} fill="url(#gVues)" />
                  </AreaChart>
                </SafeChart>
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-4 px-4 pt-2.5 text-[11px]">
                <span className="flex items-center gap-1.5 text-[#475569]"><span className="w-2 h-2 rounded-full bg-[#0369A1]" /> Prévues ({monthPubs.length})</span>
                <span className="flex items-center gap-1.5 text-[#475569]"><span className="w-2 h-2 rounded-full bg-[#4ADE80]" /> Publiées ({monthPubs.filter((p) => p.status === "publie").length})</span>
                <span className="flex items-center gap-1.5 text-[#475569]"><span className="w-2 h-2 rounded-full bg-[#B45309]" /> À valider ({pending})</span>
              </div>
              <div className="px-2 pb-2 pt-1" data-testid="cm-performance-chart">
                <SafeChart height={160}>
                  <LineChart data={daily} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="jour" tick={{ fontSize: 9, fill: "#94A3B8" }} interval={6} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} allowDecimals={false} axisLine={false} tickLine={false} />
                    <RTooltip formatter={(v, n) => [v, n === "prevues" ? "Prévues" : "Publiées"]} labelFormatter={(l) => `${l} ${format(new Date(), "MMMM", { locale: fr })}`} />
                    <Line type="monotone" dataKey="prevues" stroke="#0369A1" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="publiees" stroke="#4ADE80" strokeWidth={2} dot={false} />
                  </LineChart>
                </SafeChart>
              </div>
            </>
          )}
        </Card>

        {/* Validations en attente */}
        <Card icon={Eye} title="Validations en attente" link="/contenus?status=a_valider" linkLabel="Voir toutes" testid="cm-validations" count={dash?.pending_validation?.length ?? 0}>
          {pendingPubs.length === 0 && <p className="px-4 py-6 text-[13px] text-neutral-400">Aucune validation en attente.</p>}
          {pendingPubs.map((p) => (
            <button key={p.id} data-testid={`cm-validation-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
              <Thumb src={pubVisual(p)} name={p.title} color={clientById[p.client_id]?.color} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[#0F172A] truncate">{p.title}</p>
                <p className="text-[10px] text-neutral-400 truncate">{p.client_name} · {platformLabel(p.platform)} · {frDate(p.date)}</p>
              </div>
              <StatusBadge status="a_valider" short className="!text-[9px] !px-1.5 !py-0 shrink-0" />
            </button>
          ))}
        </Card>

        {/* Tâches du jour + Demandes récentes */}
        <div className="space-y-4">
          <Card icon={Zap} title="Tâches du jour" link="/taches" linkLabel="Voir toutes" testid="cm-tasks" count={todayTasks.length}>
            {todayTasks.length === 0 && <p className="px-4 py-4 text-[13px] text-neutral-400">Aucune tâche échue aujourd'hui.</p>}
            {todayTasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0" data-testid={`cm-task-${t.id}`}>
                <button onClick={() => completeTask(t)} data-testid={`cm-task-check-${t.id}`}
                  className="w-4 h-4 rounded-full border-2 border-neutral-300 hover:border-[#15803D] hover:bg-[#DCFCE7] transition-colors shrink-0" title="Marquer finalisée" />
                <p className="flex-1 min-w-0 text-[12px] text-[#0F172A] truncate">{t.title}</p>
                <span className={`text-[10px] whitespace-nowrap ${isPast(parseISO(t.deadline.slice(0, 10))) && !isToday(parseISO(t.deadline.slice(0, 10))) ? "text-[#BE123C] font-semibold" : "text-neutral-400"}`}>
                  {isToday(parseISO(t.deadline.slice(0, 10))) ? "Aujourd'hui" : frDate(t.deadline.slice(0, 10))}
                </span>
              </div>
            ))}
          </Card>
          <Card icon={Inbox} title="Demandes clients récentes" link="/demandes" linkLabel="Voir toutes" testid="cm-requests" count={openRequests.length}>
            {openRequests.length === 0 && <p className="px-4 py-4 text-[13px] text-neutral-400">Aucune demande en cours.</p>}
            {openRequests.map((r) => (
              <button key={r.id} data-testid={`cm-request-${r.id}`} onClick={() => navigate(`/demandes?focus=${r.id}`)}
                className="w-full flex items-center gap-2.5 px-4 py-2 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <Thumb src={clientById[r.client_id]?.brand?.logo_url} name={r.client_name} color={clientById[r.client_id]?.color} className="w-7 h-7 rounded-full" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-[#0F172A] truncate">{r.client_name}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{r.title}</p>
                </div>
                <span className="text-[10px] text-neutral-400 whitespace-nowrap">{relWhen(r.created_at)}</span>
                {r.urgency === "urgent" && <span className="w-1.5 h-1.5 rounded-full bg-[#FB7185] shrink-0" title="Urgent" />}
              </button>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ================= Vue d'ensemble — client sélectionné ================= */
const PRIO_STYLE = {
  urgente: { label: "Urgent", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  haute: { label: "Haute", cls: "bg-orange-50 text-orange-500 border-orange-200" },
  moyenne: { label: "Moyenne", cls: "bg-amber-50 text-amber-600 border-amber-200" },
  normale: { label: "Moyenne", cls: "bg-amber-50 text-amber-600 border-amber-200" },
  basse: { label: "Basse", cls: "bg-emerald-50 text-emerald-600 border-emerald-200" },
};

function CmClientOverview({ clientId, pubs, clients, onPubsChanged }) {
  const navigate = useNavigate();
  const client = clients.find((c) => c.id === clientId);
  const [stats, setStats] = useState(null);
  const [contentReqs, setContentReqs] = useState([]);
  const [feedOpen, setFeedOpen] = useState(false);
  const [pubModal, setPubModal] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setStats(null);
    api.get(`/analytics/client/${clientId}?period=month`).then((r) => setStats(r.data)).catch(() => setStats({ connected: false }));
    api.get(`/clients/${clientId}/content-requests`).then((r) => setContentReqs(r.data || [])).catch(() => {});
  }, [clientId]);

  const today = format(new Date(), "yyyy-MM-dd");
  const upcoming = useMemo(() => pubs.filter((p) => p.date >= today && p.status !== "publie")
    .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""))).slice(0, 5), [pubs, today]);
  // Feed : uniquement publications, 1re slide des carrousels et couverture des vidéos —
  // jamais les stories. Ordre de publication (récent en premier)
  const feedPubs = useMemo(() => pubs.filter((p) => isFeedVisible(p) && pubVisual(p))
    .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || ""))), [pubs]);
  const pendingVal = useMemo(() => pubs.filter((p) => p.status === "a_valider")
    .sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(0, 4), [pubs]);
  const openReqs = contentReqs.filter((r) => r.status !== "valide");
  /* KPI opérationnels (mêmes statistiques que la vue globale, scopées client) */
  const month = format(new Date(), "yyyy-MM");
  const lastMonth = format(subMonths(new Date(), 1), "yyyy-MM");
  const monthPubs = pubs.filter((p) => (p.date || "").startsWith(month));
  const lastMonthPubs = pubs.filter((p) => (p.date || "").startsWith(lastMonth));
  const reels = monthPubs.filter((p) => p.format === "reel").length;
  const reelsLast = lastMonthPubs.filter((p) => p.format === "reel").length;
  const pending = pubs.filter((p) => p.status === "a_valider").length;

  const genIdeas = async () => {
    setGenerating(true);
    try {
      await api.post(`/clients/${clientId}/ideas/generate`, {});
      toast.success("Idées générées par DGA AI — retrouvez-les dans la fiche client");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  if (!client) return null;
  const connected = stats?.connected !== false;
  const fol = stats?.followers || {};
  const kpi = (id) => (stats?.totals ? { value: stats.totals[id], delta: stats.deltas?.[id] } : null);
  const tags = [...(client.tags || []), client.sector].filter(Boolean).slice(0, 4);
  const since = client.created_at ? format(parseISO(client.created_at.slice(0, 10)), "MMM yyyy", { locale: fr }) : null;
  const isActive = (client.status || "actif") === "actif";

  const KPIS = [
    { id: "ig", label: "Abonnés Instagram", icon: Instagram, fg: "#E1306C", bg: "#FCE7F3", value: fol.instagram?.total ?? null, delta: fol.instagram?.delta },
    { id: "fb", label: "Abonnés Facebook", icon: Facebook, fg: "#1877F2", bg: "#DBEAFE", value: fol.facebook?.total ?? null, delta: fol.facebook?.delta },
    { id: "eng", label: "Taux d'engagement", icon: Heart, fg: "#E11D48", bg: "#FFE4E6", value: stats?.engagement != null ? `${Number(stats.engagement).toFixed(1).replace(".", ",")} %` : null, delta: null },
    { id: "reach", label: "Portée moyenne", icon: BarChart3, fg: "#6B21A8", bg: "#F3E8FF", value: stats?.avg_reach ?? null, delta: stats?.deltas?.reach },
  ];

  return (
    <div className="space-y-4" data-testid="cm-client-overview">
      {/* En-tête client */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-4 flex flex-wrap items-center gap-4" data-testid="cm-client-header">
        <Thumb src={client.brand?.logo_url} name={client.name} color={client.color} className="w-14 h-14 rounded-2xl" />
        <div className="min-w-0">
          <h2 className="font-heading text-xl font-bold text-[#0F172A] truncate" data-testid="cm-client-name">{client.name}</h2>
          <p className="text-xs text-neutral-400">Community Management</p>
          {tags.length > 0 && (
            <div className="flex gap-1.5 mt-1.5 flex-wrap" data-testid="cm-client-tags">
              {tags.map((t) => <span key={t} className="text-[9px] font-medium text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5">{t}</span>)}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-4 flex-wrap">
          <div className="text-right hidden sm:block">
            <p className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${isActive ? "text-emerald-600" : "text-neutral-400"}`} data-testid="cm-client-status">
              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-neutral-300"}`} /> {isActive ? "Client actif" : "Client inactif"}
            </p>
            {since && <p className="text-[10px] text-neutral-400 mt-0.5">Depuis {since}</p>}
          </div>
          <button onClick={genIdeas} disabled={generating} data-testid="cm-client-gen-ai"
            className="inline-flex items-center gap-1.5 border border-[#C7D2FE] bg-[#EEF2FF] text-[#4F46E5] rounded-xl px-3.5 py-2 text-xs font-semibold hover:bg-[#E0E7FF] transition-colors disabled:opacity-50">
            <Sparkles className={`w-3.5 h-3.5 ${generating ? "animate-pulse" : ""}`} /> {generating ? "Génération…" : "Générer avec DGA AI"}
          </button>
          <button onClick={() => setPubModal(true)} data-testid="cm-client-new-pub"
            className="inline-flex items-center gap-1.5 bg-[#2563EB] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1D4ED8] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Nouvelle publication
          </button>
        </div>
      </div>

      {/* KPI opérationnels — mêmes statistiques que la vue globale, scopées client */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cm-client-kpis-ops">
        <Kpi testid="cm-client-kpi-prevues" icon={CalendarDays} pastel={PASTEL.blue} value={monthPubs.length} label="Publications prévues"
          sub={subOf(delta(monthPubs.length, lastMonthPubs.length))} />
        <Kpi testid="cm-client-kpi-valider" icon={Eye} pastel={PASTEL.amber} value={pending} label="Contenus à valider"
          sub={{ text: "en attente client", color: "#B45309" }} />
        <Kpi testid="cm-client-kpi-reels" icon={Video} pastel={PASTEL.green} value={reels} label="Réels du mois"
          sub={subOf(delta(reels, reelsLast))} />
        <Kpi testid="cm-client-kpi-engagement" icon={CheckCircle2} pastel={PASTEL.purple}
          value={stats?.engagement != null ? `${Number(stats.engagement).toFixed(1).replace(".", ",")} %` : "—"} label="Taux d'engagement"
          sub={stats?.engagement != null
            ? { text: "données Meta réelles du mois", color: "#6B21A8" }
            : { text: connected ? "en attente de données Meta" : "compte Meta non connecté", color: "#94A3B8" }} />
        <Kpi testid="cm-client-kpi-portee" icon={Users} pastel={PASTEL.rose}
          value={kpi("reach")?.value ? formatK(kpi("reach").value) : "—"} label="Portée totale"
          sub={kpi("reach")?.delta != null
            ? { text: `${kpi("reach").delta >= 0 ? "+" : ""}${kpi("reach").delta} % vs mois dernier`, color: kpi("reach").delta >= 0 ? "#15803D" : "#BE123C" }
            : { text: connected ? "données Meta réelles" : "insights Meta indisponibles", color: "#94A3B8" }} />
      </div>

      {/* KPI réseaux — données réelles Meta uniquement */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cm-client-kpis">
        {KPIS.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid={`cm-ckpi-${c.id}`}>
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: c.bg }}>
                <c.icon className="w-4 h-4" style={{ color: c.fg }} />
              </span>
              <p className="text-[11px] font-medium text-neutral-500">{c.label}</p>
            </div>
            <p className="font-heading text-2xl font-bold mt-1.5 text-[#0F172A]" data-testid={`cm-ckpi-${c.id}-value`}>
              {c.value != null ? (typeof c.value === "number" ? formatK(c.value) : c.value) : "—"}
            </p>
            <p className="text-[10px] mt-0.5">
              {c.delta != null ? (
                <span className={`font-semibold ${c.delta >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                  {c.delta >= 0 ? "↗" : "↘"} {c.delta >= 0 ? "+" : ""}{c.delta} % <span className="text-neutral-400 font-normal">vs mois dernier</span>
                </span>
              ) : <span className="text-neutral-300">{connected ? "vs mois dernier" : "Meta non connecté"}</span>}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Prochaines publications */}
        <Card icon={CalendarDays} title="Prochaines publications" link={`/cm?tab=calendrier&client=${clientId}`} linkLabel="Voir toutes" testid="cm-client-upcoming">
          {upcoming.length === 0 && <p className="px-4 py-6 text-[13px] text-neutral-400" data-testid="cm-client-upcoming-empty">Aucune publication programmée.</p>}
          {upcoming.map((p) => (
            <button key={p.id} data-testid={`cm-client-upcoming-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
              <Thumb src={pubVisual(p)} name={p.title} color={client.color} className="w-9 h-9 rounded-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[#0F172A] truncate flex items-center gap-1">
                  {(p.channels?.instagram ?? p.platform !== "facebook")
                    ? <Instagram className="w-3 h-3 text-[#E1306C] shrink-0" />
                    : <Facebook className="w-3 h-3 text-[#1877F2] shrink-0" />}
                  <span className="truncate">{p.title}</span>
                </p>
                <p className="text-[10px] text-neutral-400">{frDate(p.date)}{p.time ? ` à ${p.time}` : ""}</p>
              </div>
              <StatusBadge status={p.status} short className="!text-[9px] !px-1.5 !py-0 shrink-0" />
            </button>
          ))}
        </Card>

        {/* Feed Instagram — visuels 4:5 dans l'ordre de publication, extensible */}
        <Card icon={Instagram} title="Feed Instagram" testid="cm-client-feed"
          action={<button onClick={() => setFeedOpen(true)} data-testid="cm-client-feed-expand"
            className="text-[11px] font-semibold text-[#0369A1] inline-flex items-center gap-1 hover:underline">
            <Maximize2 className="w-3 h-3" /> Agrandir
          </button>}>
          {feedPubs.length === 0 && <p className="px-4 py-6 text-[13px] text-neutral-400" data-testid="cm-client-feed-empty">Aucun visuel publié pour le moment.</p>}
          <div className="grid grid-cols-3 gap-1.5 p-3">
            {feedPubs.slice(0, 6).map((p) => (
              <button key={p.id} data-testid={`cm-client-feed-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)} title={p.title}
                className="relative rounded-lg overflow-hidden group">
                {pubVisualFull(p).video ? (
                  <video src={pubVisual(p)} muted playsInline preload="metadata"
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <img src={pubVisual(p)} alt={p.title} loading="lazy"
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform" />
                )}
                {p.status === "publie" && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-white" title="Publié" />}
              </button>
            ))}
          </div>
        </Card>

        {/* Contenu à fournir (client) */}
        <Card icon={FileText} title="Contenu à fournir (client)" link={`/clients/${clientId}?tab=apercu`} linkLabel="Voir tout" testid="cm-client-content-reqs" count={openReqs.length}>
          {openReqs.length === 0 && <p className="px-4 py-6 text-[13px] text-neutral-400" data-testid="cm-client-reqs-empty">Aucun contenu à fournir.</p>}
          {openReqs.slice(0, 5).map((r) => {
            const pr = PRIO_STYLE[r.priority] || PRIO_STYLE.normale;
            return (
              <button key={r.id} data-testid={`cm-client-req-${r.id}`} onClick={() => navigate(`/clients/${clientId}?tab=apercu`)}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <span className="w-4 h-4 rounded-full border-2 border-neutral-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-[#0F172A] truncate">{r.title}</p>
                  <p className="text-[10px] text-neutral-400 flex items-center gap-1.5 mt-0.5">
                    <span className={`font-semibold border rounded-full px-1.5 py-px ${pr.cls}`}>{pr.label}</span>
                    {r.deadline && <span>Pour le {frDate(r.deadline)}</span>}
                  </p>
                </div>
              </button>
            );
          })}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Performances récentes — données réelles Meta */}
        <Card icon={BarChart3} title="Performances récentes" link={`/cm?tab=analyse&client=${clientId}`} linkLabel="Voir toutes les statistiques" testid="cm-client-perf">
          {!connected ? (
            <div className="px-4 py-6 text-center" data-testid="cm-client-perf-nometa">
              <p className="text-[12px] text-neutral-400 mb-2">Aucun compte Meta relié — connectez-le pour voir les performances réelles.</p>
              <button onClick={() => navigate(`/clients/${clientId}?tab=apercu`)} data-testid="cm-client-perf-connect"
                className="text-[11px] font-semibold text-[#1D4ED8] hover:underline">Connecter Meta depuis la fiche client</button>
            </div>
          ) : !stats ? <p className="px-4 py-6 text-[13px] text-neutral-400">Chargement…</p> : (
            <>
              <div className="flex gap-4 px-4 pt-2.5 text-[11px] flex-wrap" data-testid="cm-client-perf-totals">
                {["reach", "impressions", "interactions", "clicks"].map((id, i) => {
                  const k = kpi(id);
                  const labels = { reach: "Portée", impressions: "Impressions", interactions: "Interactions", clicks: "Clics lien" };
                  return (
                    <span key={id} className="flex items-center gap-1.5 text-[#475569]">
                      <span className="w-2 h-2 rounded-full" style={{ background: ["#0369A1", "#22C55E", "#B45309", "#6B21A8"][i] }} />
                      {labels[id]} <strong className="text-[#0F172A]">{k ? formatK(k.value) : "—"}</strong>
                      {k?.delta != null && <span className={`font-semibold ${k.delta >= 0 ? "text-emerald-600" : "text-rose-500"}`}>{k.delta >= 0 ? "↗" : "↘"} {Math.abs(k.delta)} %</span>}
                    </span>
                  );
                })}
              </div>
              <div className="px-2 pb-2 pt-1" data-testid="cm-client-perf-chart">
                <SafeChart height={150}>
                  <AreaChart data={(stats.perf_series || []).map((d) => ({ jour: parseInt(d.day.slice(8), 10), portee: d.reach }))} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="jour" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <RTooltip formatter={(v) => [formatK(v), "Portée"]} labelFormatter={(l) => `${l} ${format(new Date(), "MMMM", { locale: fr })}`} />
                    <Area type="monotone" dataKey="portee" stroke="#0369A1" strokeWidth={2} fill="#E0F2FE" />
                  </AreaChart>
                </SafeChart>
              </div>
            </>
          )}
        </Card>

        {/* Contenus en attente de validation */}
        <Card icon={Eye} title="Contenus en attente de validation" link={`/cm?tab=contenus&client=${clientId}`} linkLabel="Voir toutes" testid="cm-client-pending" count={pendingVal.length}>
          {pendingVal.length === 0 && <p className="px-4 py-6 text-[13px] text-neutral-400" data-testid="cm-client-pending-empty">Aucun contenu en attente.</p>}
          {pendingVal.map((p) => (
            <button key={p.id} data-testid={`cm-client-pending-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
              <Thumb src={pubVisual(p)} name={p.title} color={client.color} className="w-9 h-9 rounded-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-[#0F172A] truncate">{p.title}</p>
                <p className="text-[10px] text-neutral-400 truncate">Publication {platformLabel(p.platform)} · {frDate(p.date)}</p>
              </div>
              <StatusBadge status="a_valider" short className="!text-[9px] !px-1.5 !py-0 shrink-0" />
            </button>
          ))}
        </Card>
      </div>

      {/* Feed agrandi — ensemble des publications, visuels 1080×1350 */}
      <Dialog open={feedOpen} onOpenChange={setFeedOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="cm-client-feed-modal" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Instagram className="w-4 h-4 text-[#E1306C]" /> Feed Instagram — {client.name}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-neutral-400 -mt-1">Visuels 1080 × 1350 px, 3 par ligne, dans l'ordre de publication (plus récente en haut à gauche, plus ancienne en bas à droite).</p>
          {feedPubs.length === 0 && <p className="py-8 text-center text-[13px] text-neutral-400">Aucun visuel publié pour le moment.</p>}
          <div className="grid grid-cols-3 gap-2">
            {feedPubs.map((p) => (
              <button key={p.id} data-testid={`cm-client-feed-modal-${p.id}`} onClick={() => { setFeedOpen(false); navigate(`/publications/${p.id}`); }}
                className="relative rounded-lg overflow-hidden group">
                {pubVisualFull(p).video ? (
                  <video src={pubVisual(p)} muted playsInline preload="metadata"
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <img src={pubVisual(p)} alt={p.title} loading="lazy"
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform" />
                )}
                <span className="absolute bottom-1 left-1 right-1 text-[9px] text-white bg-black/50 rounded px-1 py-0.5 truncate text-left">{p.title}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <NewPublicationModal open={pubModal} setOpen={setPubModal} clients={clients} defaultClientId={clientId} onCreated={onPubsChanged} />
    </div>
  );
}

/* ================= Demandes ================= */
function CmDemandes({ requests }) {
  const navigate = useNavigate();
  const open = requests.filter((r) => !["terminee", "annulee", "refusee"].includes(r.status));
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="cm-demandes">
      {open.length === 0 && <p className="px-5 py-8 text-sm text-neutral-400">Aucune demande en cours.</p>}
      {open.map((r) => {
        const st = clientRequestStatus(r.status);
        return (
          <button key={r.id} data-testid={`cm-demande-${r.id}`} onClick={() => navigate(`/demandes?focus=${r.id}`)}
            className="w-full flex items-center gap-3 px-5 py-3 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
            <span className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center text-[10px] font-bold text-neutral-500 shrink-0">{initials(r.client_name)}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-[#0F172A] truncate">{r.title}</p>
              <p className="text-[11px] text-neutral-400 truncate">{r.client_name} · {relWhen(r.created_at)}</p>
            </div>
            {r.urgency === "urgent" && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border" style={{ background: PASTEL.rose.bg, color: PASTEL.rose.text, borderColor: PASTEL.rose.border }}>Urgent</span>}
            <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 whitespace-nowrap ${st.cls}`}>{st.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ================= Page Community Management ================= */
const CM_TABS = [
  { id: "overview", label: "Vue d'ensemble", Icon: LayoutGrid },
  { id: "calendrier", label: "Calendrier", Icon: CalendarDays },
  { id: "contenus", label: "Contenus", Icon: ImageIcon },
  { id: "analyse", label: "Analyse", Icon: BarChart3 },
  { id: "demandes", label: "Demandes", Icon: Inbox },
];

export default function CommunityManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "overview";
  const tab = (rawTab === "analyses" || rawTab === "rapports") ? "analyse" : rawTab;
  const setTab = (t) => setSearchParams((p) => { const n = new URLSearchParams(p); if (t === "overview") n.delete("tab"); else n.set("tab", t); return n; }, { replace: true });

  const [dash, setDash] = useState(null);
  const [pubs, setPubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);

  /* Sélection client globale (paramètre URL « client », partagé avec les onglets
     Analyse et Contenus) — « Tous les clients » = vue globale. */
  const clientId = searchParams.get("client") || "";
  const setClientSel = (id) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    if (id) n.set("client", id); else n.delete("client");
    return n;
  }, { replace: true });
  const cmClients = useMemo(() => clients.filter((c) => !c.archived && isCmClient(c)), [clients]);
  const selPubs = useMemo(() => clientId ? pubs.filter((p) => p.client_id === clientId) : pubs, [pubs, clientId]);
  const selRequests = useMemo(() => clientId ? requests.filter((r) => r.client_id === clientId) : requests, [requests, clientId]);
  const selClients = useMemo(() => clientId ? clients.filter((c) => c.id === clientId) : clients, [clients, clientId]);
  const selDash = useMemo(() => {
    if (!clientId || !dash) return dash;
    return { ...dash,
      tasks: (dash.tasks || []).filter((t) => t.client_id === clientId),
      pending_validation: (dash.pending_validation || []).filter((p) => p.client_id === clientId) };
  }, [dash, clientId]);

  useEffect(() => { api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {}); }, []);
  const reloadDash = () => api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
  const reloadPubs = () => api.get("/publications").then((r) => setPubs(r.data || [])).catch(() => {});
  useEffect(() => { reloadPubs(); }, []);
  // Rafraîchissement automatique : toute modification de publication (visuel,
  // slides, titre…) met à jour le feed, le calendrier du jour et les cartes.
  useEffect(() => {
    const h = () => reloadPubs();
    window.addEventListener("sf:pubs", h);
    return () => window.removeEventListener("sf:pubs", h);
  }, []);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/client-requests").then((r) => {
      const list = (r.data || []).slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRequests(list);
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="cm-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Community Management</h1>
          <p className="text-neutral-500 text-xs mt-1">Gérez vos contenus, validations et performances client.</p>
        </div>
        <label className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-xl shadow-sm px-3 py-2">
          <Users className="w-4 h-4 text-neutral-400 shrink-0" />
          <select value={clientId} onChange={(e) => setClientSel(e.target.value)} data-testid="cm-client-select"
            className="text-xs font-semibold bg-transparent text-[#0F172A] min-w-[170px] focus:outline-none cursor-pointer">
            <option value="">Tous les clients</option>
            {cmClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>

      <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto" data-testid="cm-tabs">
        {CM_TABS.map((t) => (
          <button key={t.id} data-testid={`cm-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-[#0B132B] font-semibold text-[#0F172A]" : "border-transparent text-neutral-400 hover:text-neutral-900"}`}>
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
        <Link to="/cm/pilotage" data-testid="cm-tab-pilotage"
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px border-transparent text-[#1D4ED8] hover:text-[#1E40AF] font-semibold transition-colors">
          Pilotage de contenu
        </Link>
      </div>

      {tab === "overview" && (clientId
        ? <CmClientOverview clientId={clientId} pubs={selPubs} clients={clients} onPubsChanged={reloadPubs} />
        : <CmOverview dash={selDash} pubs={selPubs} clients={selClients} requests={selRequests} onGoTab={setTab} onTaskDone={reloadDash} />)}
      {tab === "calendrier" && <GlobalCalendar embedded clientId={clientId} />}
      {tab === "contenus" && <Contenus embedded />}
      {tab === "analyse" && <CmAnalyse clients={clients} />}
      {tab === "demandes" && <CmDemandes requests={selRequests} />}
    </div>
  );
}
